'use client';

/**
 * Netflix-style hover preview, learning-aware. Three states:
 *
 * <ul>
 *   <li><b>Resting</b>: 16:9 dark poster with cover image, level chip,
 *       title/instructor/duration overlay, brand-red progress bar if
 *       in progress, subtle 1.02× scale on cursor enter.</li>
 *   <li><b>Preview</b> (opens after 600ms hover delay): fixed-position
 *       panel anchored to the card's viewport rect. Muted autoplay
 *       {@code <video>} (or embedded player for YouTube/Cinema8 preview
 *       URLs), mute toggle, Play CTA + expand toggle, a watch-later/
 *       favorite action row spread against level/enrolled/duration meta,
 *       a 3-line title, an instructor block (avatar + name), a clamped
 *       description, and a progress strip when in progress.</li>
 *   <li><b>Detail</b> (opens via chevron): same popover, taller,
 *       scrolls. Adds long-form description, outcomes, target audience,
 *       requirements, and chapter/lesson tree.</li>
 * </ul>
 *
 * <p>Exit grace: 100ms forgiveness for cursor wobble; ESC closes for
 * keyboard users. Touch devices (pointer:coarse) skip the popover —
 * tap navigates directly via the underlying anchor.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import {
  Bookmark,
  BookmarkCheck,
  Clock,
  Heart,
  Users,
  ArrowDown,
  BookOpen,
  Eye,
  ListChecks,
  ClipboardCheck,
  FileCheck,
  Medal,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PortalLink,
  useCurrentUser,
  useUserResourceSource,
  useLearningSource,
  type LearningCourseDetail,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { cardReset } from '../_shared/styles';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { Stars } from '../_shared/Stars';
import { isEmbedUrl, PreviewEmbed } from '../_shared/CoursePreviewMedia';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

const HOVER_ENTER_DELAY = 600;
const HOVER_EXIT_DELAY = 100;
const POPOVER_SCALE = 1.45;
const VIEWPORT_MARGIN = 16;
const DETAIL_MAX_VH = 0.85;
const DETAIL_MIN_HEIGHT = 540;

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    setCoarse(mq.matches);
    const handler = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return coarse;
}

/**
 * "Add to favorites" — wired to {@code UserResourceSource.wishlist}. Same
 * optimistic-toggle/error-rollback shape as {@code ClassicNetflixZoomCourseCard}
 * / {@code BorderlessCourseCard}'s {@code useFavoriteToggle}.
 */
function useFavoriteToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.wishlist) return null;
      return (await ur.wishlist.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.wishlist);
  const favorited = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!favorited);
    try {
      const res = await ur!.wishlist!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const active = Boolean(res.active);
        setOptimistic(active);
        toast.success(
          t(active ? 'message.addedToFavoriteCourseList' : 'message.removedFromFavoriteCourseList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[CinematicCourseCard] wishlist toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, favorited, toggling, toggle };
}

/**
 * "Watch later" — wired to {@code UserResourceSource.courseWatchLater}. Same
 * shape as {@code useFavoriteToggle} above.
 */
function useWatchLaterToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.courseWatchLater) return null;
      return (await ur.courseWatchLater.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.courseWatchLater);
  const active = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!active);
    try {
      const res = await ur!.courseWatchLater!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const next = Boolean(res.active);
        setOptimistic(next);
        toast.success(
          t(next ? 'message.addedToWatchLaterList' : 'message.removedFromWatchLaterList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[CinematicCourseCard] watch-later toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, active, toggling, toggle };
}

/** "2.1h" from totalHours, or the pre-formatted duration string. */
function formatDurationLabel(duration?: string, totalHours?: number): string | null {
  if (duration) return duration;
  if (typeof totalHours === 'number' && totalHours > 0) return `${totalHours}h`;
  return null;
}

/** Mirrors the course-detail hero's `translateLearningType` — same i18n
 *  keys, so the badge text matches what the full course-detail page shows
 *  for the same course. */
function translateLearningType(
  type: string | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (type === 'plan') return t('label.developmentPlanLearning', 'Development plan');
  if (type === 'REMOTE_COURSE') return t('title.eLearning', 'E-learning');
  if (type === 'PROGRAM') return t('title.program', 'Program');
  return t('title.inClass', 'In-class');
}

export function CinematicCourseCard({
  course: c,
  showRating = true,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  showRating?: boolean;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [state, setState] = useState<'idle' | 'pending' | 'open'>('idle');
  const [muted, setMuted] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const inProgress = showProgress && typeof c.progress === 'number'; //&& c.progress > 0 && c.progress < 100;
  const isCoarse = useCoarsePointer();
  const favorite = useFavoriteToggle(c.id, c.title);
  const watchLater = useWatchLaterToggle(c.id, c.title);

  function clearTimers() {
    if (enterTimer.current) {
      clearTimeout(enterTimer.current);
      enterTimer.current = null;
    }
    if (exitTimer.current) {
      clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
  }

  function captureAnchor() {
    const el = cardRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  function onPointerEnter() {
    if (isCoarse) return;
    clearTimers();
    setState('pending');
    enterTimer.current = setTimeout(() => {
      const a = captureAnchor();
      if (a) {
        setAnchor(a);
        setState('open');
      }
    }, HOVER_ENTER_DELAY);
  }

  function onPointerLeave() {
    if (isCoarse) return;
    clearTimers();
    if (state === 'pending') {
      setState('idle');
      return;
    }
    if (expanded) return;
    exitTimer.current = setTimeout(() => {
      setState('idle');
    }, HOVER_EXIT_DELAY);
  }

  useEffect(() => {
    if (state !== 'open') return;
    function reAnchor() {
      const a = captureAnchor();
      if (a) setAnchor(a);
    }
    window.addEventListener('scroll', reAnchor, { passive: true, capture: true });
    window.addEventListener('resize', reAnchor);
    return () => {
      window.removeEventListener('scroll', reAnchor, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', reAnchor);
    };
  }, [state]);

  useEffect(() => {
    if (state !== 'open') return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      clearTimers();
      if (expanded) setExpanded(false);
      else setState('idle');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, expanded]);

  useEffect(() => {
    if (state === 'idle') setExpanded(false);
  }, [state]);

  useEffect(() => {
    if (state !== 'open' || !expanded) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      let n: Node | null = target;
      while (n) {
        if (n instanceof HTMLElement && n.dataset?.cinematicPopover) return;
        n = n.parentNode;
      }
      setExpanded(false);
      setState('idle');
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [state, expanded]);

  useEffect(() => () => clearTimers(), []);

  return (
    <>
      <Card asChild>
        <PortalLink
          ref={cardRef}
          href={c.href}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          style={{
            ...cardReset,
            position: 'relative',
            display: 'block',
            aspectRatio: 'var(--app-course-image-aspect-ratio, 16 / 9)',
            borderRadius: 'var(--radius-md, 8px)',
            color: 'var(--color-text-on-brand, #fff)',
            textDecoration: 'none',
            scrollSnapAlign: 'start',
            boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.12))',
            transform: state === 'pending' ? 'scale(1.02)' : 'scale(1)',
            transition: 'transform 200ms ease, box-shadow 200ms ease, opacity 200ms ease',
            opacity: state === 'open' ? 0.4 : 1,
            zIndex: state === 'pending' ? 5 : 1,
            cursor: 'pointer',
          }}
        >
          <RestingContent
            course={c}
            inProgress={inProgress}
            showProgressText={showProgressText}
            progressTextPlace={progressTextPlace}
          />
        </PortalLink>
      </Card>

      {state === 'open' && anchor && (
        <PreviewPopover
          course={c}
          inProgress={inProgress}
          showRating={showRating}
          anchor={anchor}
          muted={muted}
          expanded={expanded}
          favorite={favorite}
          watchLater={watchLater}
          onToggleMute={() => setMuted((m) => !m)}
          onToggleExpand={() => setExpanded((e) => !e)}
          onMouseEnter={() => clearTimers()}
          onMouseLeave={onPointerLeave}
        />
      )}
    </>
  );
}

CinematicCourseCard.gridSpec = {
  kind: 'rail',
  cardWidth: 'clamp(220px, 35vw, 320px)',
  gap: 'clamp(10px, 1.5vw, 16px)',
} satisfies CardGridSpec;

function RestingContent({
  course: c,
  inProgress,
  showProgressText,
  progressTextPlace,
}: {
  course: CourseCardProps;
  inProgress: boolean;
  showProgressText: boolean;
  progressTextPlace: ProgressTextPlace;
}) {
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', overflow: 'hidden' }}>
        <CourseCoverImage
          image={typeof c.image === 'string' ? c.image : undefined}
          resizeTo="680x0"
        />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0) 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: 'clamp(10px, 2vw, 10px)',
          borderRadius: 'inherit',
          pointerEvents: 'none',
        }}
      >
        <LevelBadge
          level={c.level}
          variant="solid"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            borderRadius: '8px',
            padding: 'var(--space-1, 8px) var(--space-2, 16px)',
            fontSize: 'var(--font-size-xs, 14px)',
          }}
        />

        <div
          style={{
            fontSize: 'clamp(13px, 1.6vw, 14px)',
            fontWeight: 600,
            lineHeight: 1.3,
            textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            color: 'var(--color-text-primary-inverse, #fff)',
          }}
        >
          {c.title}
        </div>
        {inProgress && (
          <CourseProgressBar
            progress={c.progress!}
            showText={showProgressText}
            textPlace={progressTextPlace}
          />
        )}
      </div>
    </>
  );
}

interface ToggleState {
  available: boolean;
  toggling: boolean;
  toggle: () => void | Promise<void>;
}

function PreviewPopover({
  course: c,
  inProgress,
  showRating = true,
  anchor,
  muted,
  expanded,
  favorite,
  watchLater,
  onToggleMute,
  onToggleExpand,
  onMouseEnter,
  onMouseLeave,
}: {
  course: CourseCardProps;
  inProgress: boolean;
  showRating?: boolean;
  anchor: AnchorRect;
  muted: boolean;
  expanded: boolean;
  favorite: ToggleState & { favorited: boolean };
  watchLater: ToggleState & { active: boolean };
  onToggleMute: () => void;
  onToggleExpand: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const { t } = useTranslation('common');
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageUrl = typeof c.image === 'string' ? resizeImageUrl(c.image, '680x0') : undefined;
  const isEmbed = Boolean(c.previewVideoUrl) && isEmbedUrl(c.previewVideoUrl!);
  const durationLabel = formatDurationLabel(c.duration, c.totalHours);

  // The list-level `course` prop only ever carries description/chapters/etc.
  // when the host already inlined them (rare — they're heavy per-item
  // fields). Expanding is the signal to go fetch the rest from the host's
  // learning data source, same contract the `/learn/[courseId]` page uses.
  const learningSource = useLearningSource();
  const hasInlineDetail = Boolean(
    c.description || c.outcomes?.length || c.requirements?.length || c.chapters?.length,
  );
  const detailState = useAsyncResource<LearningCourseDetail>(
    async (signal) => {
      if (!expanded || hasInlineDetail || !learningSource) return null;
      return learningSource.fetchCourseDetail(c.id, signal);
    },
    [expanded, hasInlineDetail, learningSource, c.id],
  );
  const detail = detailState.kind === 'loaded' ? detailState.data : null;
  const detailLoading = expanded && !hasInlineDetail && detailState.kind === 'loading';

  const targetWidth = anchor.width * POPOVER_SCALE;
  const previewHeight = anchor.height * POPOVER_SCALE * 1.9;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;

  const detailHeight = Math.max(DETAIL_MIN_HEIGHT, Math.min(vh * DETAIL_MAX_VH, 760));
  const targetHeight = expanded ? detailHeight : previewHeight;

  let left = anchor.left + (anchor.width - targetWidth) / 2;
  let top = expanded
    ? anchor.top + (anchor.height - anchor.height * POPOVER_SCALE) / 2
    : anchor.top + (anchor.height - previewHeight) / 2;

  if (left + targetWidth + VIEWPORT_MARGIN > vw) left = vw - targetWidth - VIEWPORT_MARGIN;
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
  if (top + targetHeight + VIEWPORT_MARGIN > vh) top = vh - targetHeight - VIEWPORT_MARGIN;
  if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        /* autoplay blocked — poster fallback */
      });
    }
  }, [c.previewVideoUrl]);

  return (
    <div
      data-cinematic-popover
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top,
        left,
        width: targetWidth,
        height: expanded ? detailHeight : 'auto',
        maxHeight: expanded ? detailHeight : previewHeight,
        zIndex: 1000,
        background: 'var(--color-surface-card, #fff)',
        borderRadius: 'var(--radius-lg, 8px)',
        overflow: 'hidden',
        color: 'var(--color-text-primary, #1c1d1f)',
        boxShadow: 'var(--shadow-2xl, 0 36px 72px rgba(0,0,0,0.22))',
        display: 'flex',
        flexDirection: 'column',
        animation: 'lms-cinematic-open 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        transition:
          'height 280ms cubic-bezier(0.2, 0.8, 0.2, 1), top 280ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        transformOrigin: 'center center',
      }}
    >
      <style>{`
        @keyframes lms-cinematic-open {
          from { transform: scale(${1 / POPOVER_SCALE}); opacity: 0.4; }
          to   { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <PortalLink
        href={c.href}
        style={{
          position: 'relative',
          aspectRatio: 'var(--app-course-image-aspect-ratio, 16 / 9)',
          flexShrink: 0,
          display: 'block',
          textDecoration: 'none',
        }}
      >
        {c.previewVideoUrl && isEmbed ? (
          <PreviewEmbed src={c.previewVideoUrl} title={c.title} poster={imageUrl} />
        ) : c.previewVideoUrl ? (
          <video
            ref={videoRef}
            src={c.previewVideoUrl}
            autoPlay
            muted={muted}
            loop
            playsInline
            preload="auto"
            poster={imageUrl}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <CourseCoverImage
            image={typeof c.image === 'string' ? c.image : undefined}
            resizeTo="680x0"
          />
        )}

        {c.previewVideoUrl && !isEmbed && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleMute();
            }}
            aria-label={
              muted
                ? t('label.unmutePreview', 'Unmute preview')
                : t('label.mutePreview', 'Mute preview')
            }
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-full, 50%)',
              border: '1px solid rgba(255,255,255,0.6)',
              background: 'rgba(0,0,0,0.55)',
              color: 'var(--color-text-on-brand, #fff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <MuteIcon muted={muted} />
          </button>
        )}
      </PortalLink>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: 'var(--space-2-5, 10px) var(--space-3, 12px) var(--space-2-5, 10px)',
            flexShrink: 0,
            borderBottom: expanded
              ? '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))'
              : 'none',
            overflowY: expanded ? 'visible' : 'auto',
            minHeight: 0,
          }}
        >
          {/* 1 — Title (2 lines) ⟷ expand toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 8,
              color: 'var(--color-text-primary, #1c1d1f)',
            }}
          >
            <PortalLink
              href={c.href}
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 16px)',
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'inherit',
                textDecoration: 'none',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {c.title}
            </PortalLink>
            <ActionButton
              aria-label={
                expanded ? t('label.hideDetails', 'Hide details') : t('label.moreInfo', 'More info')
              }
              aria-expanded={expanded}
              aria-pressed={expanded}
              active={expanded}
              onClick={onToggleExpand}
              style={{ flexShrink: 0 }}
            >
              {expanded ? (
                <ArrowDown size={16} style={{ transform: 'rotate(180deg)' }} />
              ) : (
                <ArrowDown size={16} />
              )}
            </ActionButton>
          </div>

          {/* 2 — Watchlater / favorite ⟷ level / member / duration */}
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ActionButton
                active={watchLater.active}
                aria-label={t('label.watchLater', 'Watch later')}
                aria-pressed={watchLater.active}
                disabled={!watchLater.available || watchLater.toggling}
                onClick={() => void watchLater.toggle()}
              >
                {watchLater.active ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
              </ActionButton>
              <ActionButton
                active={favorite.favorited}
                aria-label={t('title.favorites', 'Favorites')}
                aria-pressed={favorite.favorited}
                disabled={!favorite.available || favorite.toggling}
                onClick={() => void favorite.toggle()}
              >
                <Heart size={15} fill={favorite.favorited ? 'currentColor' : 'none'} />
              </ActionButton>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                fontSize: 'var(--font-size-xs, 11px)',
                color: 'var(--color-text-secondary, #4a4a4a)',
                flexWrap: 'wrap',
              }}
            >
              {showRating && typeof c.ratingAverage === 'number' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <Stars value={c.ratingAverage} size={11} />
                </span>
              )}
              <LevelBadge
                level={c.level}
                variant="solid"
                style={{
                  borderRadius: '8px',
                  padding: 'var(--space-1, 8px) var(--space-2, 16px)',
                  fontSize: 'var(--font-size-xs, 14px)',
                }}
              />
              {typeof c.enrolled === 'number' && c.enrolled > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Users size={12} />
                  {c.enrolled.toLocaleString()}
                </span>
              )}
              {durationLabel && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={12} />
                  {durationLabel}
                </span>
              )}
            </div>
          </div>

          {/* 3 — Description (2 lines) */}
          {!expanded && c.description && (
            <p
              style={{
                margin: 0,
                marginTop: 10,
                fontSize: 'var(--font-size-sm, 13px)',
                lineHeight: 1.5,
                color: 'var(--color-text-secondary, #4a4a4a)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {c.description}
            </p>
          )}

          {/* 4 — Progress */}
          {!expanded && inProgress && (
            <div style={{ marginTop: 10 }}>
              <ProgressLine progress={c.progress!} totalHours={c.totalHours} />
            </div>
          )}
        </div>

        {expanded && (
          <DetailPanel course={c} inProgress={inProgress} detail={detail} loading={detailLoading} />
        )}
      </div>
    </div>
  );
}

type DetailTab = 'main' | 'faq' | 'documents' | 'comments';

function DetailPanel({
  course: c,
  inProgress,
  detail,
  loading,
}: {
  course: CourseCardProps;
  inProgress: boolean;
  /** Fetched via {@code LearningSource.fetchCourseDetail} when the list-level
   *  `course` prop didn't already carry description/chapters/etc. */
  detail: LearningCourseDetail | null;
  loading: boolean;
}) {
  const { t } = useTranslation('common');
  const learningSource = useLearningSource();
  const [tab, setTab] = useState<DetailTab>('main');

  const hasFaqTab = Boolean(learningSource?.fetchCourseFaqs);
  const hasDocumentsTab = Boolean(learningSource?.fetchCourseDocuments);
  const hasCommentsTab = Boolean(learningSource?.fetchCourseComments);

  const description = c.description;
  const detailDescription = detail?.description;
  const outcomes = c.outcomes ?? detail?.outcomes;
  const targetAudience = c.targetAudience ?? detail?.targetAudience;
  const requirements = c.requirements ?? detail?.requirements;
  const chapters = c.chapters ?? detail?.chapters;
  const instructorName = c.instructor ?? c.trainers ?? detail?.instructor?.name;
  const instructorAvatar = c.instructorAvatar ?? detail?.instructor?.avatar;
  const hasCertificate = c.hasCertificate ?? detail?.hasCertificate;
  const certificateBadgeUrl = c.certificateBadgeUrl;
  const totalLessons = (chapters ?? []).reduce((sum, ch) => sum + (ch.lessons?.length ?? 0), 0);
  const isEmpty =
    !description &&
    !outcomes?.length &&
    !targetAudience?.length &&
    !requirements?.length &&
    !chapters?.length;

  const tabs: Array<{ id: DetailTab; label: string }> = [
    { id: 'main', label: t('label.description', 'Description') },
    ...(hasFaqTab ? [{ id: 'faq' as const, label: t('title.faq', 'FAQ') }] : []),
    ...(hasDocumentsTab
      ? [{ id: 'documents' as const, label: t('title.documents', 'Documents') }]
      : []),
    ...(hasCommentsTab
      ? [{ id: 'comments' as const, label: t('label.comments', 'Comments') }]
      : []),
  ];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{`
        [data-cinematic-popover] *::-webkit-scrollbar { width: 6px; }
        [data-cinematic-popover] *::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.18);
          border-radius: 3px;
        }
        [data-cinematic-popover] img { max-width: 100%; height: auto; border-radius: var(--radius-sm, 4px); }
        [data-cinematic-popover] p { margin: 0 0 8px; }
        [data-cinematic-popover] p:last-child { margin-bottom: 0; }
      `}</style>

      {tabs.length > 1 && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            padding: '0 var(--space-3-5, 14px)',
            borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
            overflowX: 'auto',
            flexShrink: 0,
          }}
        >
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTab(tb.id);
              }}
              style={{
                appearance: 'none',
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${tab === tb.id ? 'var(--color-brand-500, #e50914)' : 'transparent'}`,
                color:
                  tab === tb.id
                    ? 'var(--color-text-primary, #1c1d1f)'
                    : 'var(--color-text-tertiary, #6a6f73)',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: tab === tb.id ? 700 : 600,
                padding: '10px 6px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {tb.label}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: 'var(--space-3, 12px) var(--space-3-5, 14px) var(--space-3-5, 14px)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent',
        }}
      >
        {tab === 'main' && (
          <>
            {inProgress && (
              <div style={{ marginBottom: 14 }}>
                <ProgressLine progress={c.progress!} totalHours={c.totalHours} />
              </div>
            )}

            {(detail?.learningLevel ||
              detail?.learningType ||
              detail?.hasPreview ||
              detail?.hasPreTest ||
              detail?.hasPostTest ||
              detail?.hasEvaluationForm ||
              hasCertificate) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                {/* {detail?.learningLevel && (
                  <LevelBadge
                    level={detail.learningLevel}
                    variant="solid"
                    style={{ fontSize: 'var(--font-size-xs, 11px)', padding: '3px 8px' }}
                  />
                )} */}
                {detail?.learningType && (
                  <InfoBadge
                    icon={<BookOpen size={11} />}
                    label={translateLearningType(detail.learningType, t)}
                  />
                )}
                {detail?.hasPreview && (
                  <InfoBadge icon={<Eye size={11} />} label={t('label.watchPreview', 'Preview')} />
                )}
                {detail?.hasPreTest && (
                  <InfoBadge
                    icon={<ListChecks size={11} />}
                    label={t('label.preTest', 'Pre-test')}
                  />
                )}
                {detail?.hasPostTest && (
                  <InfoBadge
                    icon={<ClipboardCheck size={11} />}
                    label={t('label.finalTest', 'Final test')}
                  />
                )}
                {detail?.hasEvaluationForm && (
                  <InfoBadge
                    icon={<FileCheck size={11} />}
                    label={t('label.evaluationForm', 'Evaluation form')}
                  />
                )}
                {hasCertificate &&
                  (certificateBadgeUrl ? (
                    <img
                      src={certificateBadgeUrl}
                      alt={t('label.certificate', 'Certificate')}
                      title={t('label.certificate', 'Certificate')}
                      style={{ height: 24, width: 'auto', objectFit: 'contain' }}
                    />
                  ) : (
                    <InfoBadge
                      icon={<Medal size={11} />}
                      label={t('label.certificate', 'Certificate')}
                    />
                  ))}
              </div>
            )}

            {detail?.trainers && detail.trainers.length > 0 ? (
              <Section title={t('label.trainers', 'Trainers')}>
                <PeopleList people={detail.trainers} />
              </Section>
            ) : (
              instructorName && (
                <div style={{ marginBottom: 14 }}>
                  <PeopleList
                    people={[{ id: 'instructor', name: instructorName, avatar: instructorAvatar }]}
                  />
                </div>
              )
            )}

            {detail?.coordinators && detail.coordinators.length > 0 && (
              <Section title={t('label.coordinators', 'Coordinators')}>
                <PeopleList people={detail.coordinators} />
              </Section>
            )}

            {loading && <TabStatus text={t('label.loading', 'Loading…')} />}
            {!loading && isEmpty && (
              <TabStatus text={t('title.recordNotFound', { defaultValue: 'No records found.' })} />
            )}

            {detailDescription && (
              <Section title={t('label.description', 'Description')}>
                <div
                  style={{
                    fontSize: '11.5px',
                    lineHeight: 1.5,
                    color: 'var(--color-text-secondary, #4a4a4a)',
                  }}
                >
                  <RichHtml value={detailDescription} />
                </div>
              </Section>
            )}

            {outcomes && outcomes.length > 0 && (
              <Section title={t('label.outcomes', 'Outcomes')}>
                <ul style={listStyle}>
                  {outcomes.map((o, i) => (
                    <li key={i} style={listItemStyle}>
                      <CheckIcon />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {targetAudience && targetAudience.length > 0 && (
              <Section title={t('label.targetAudience', 'Audience')}>
                <ul style={listStyle}>
                  {targetAudience.map((a, i) => (
                    <li key={i} style={listItemStyle}>
                      <DotIcon />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* {requirements && requirements.length > 0 && (
              <Section title={t('label.requirements', 'Requirements')}>
                <ul style={listStyle}>
                  {requirements.map((r, i) => (
                    <li key={i} style={listItemStyle}>
                      <DotIcon />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )} */}

            {chapters && chapters.length > 0 && (
              <Section
                title={t('title.curriculum', 'Curriculum')}
                subtitle={t('label.sectionsAndLessons', {
                  defaultValue: '{{sections}} sections · {{lessons}} lessons',
                  sections: chapters.length,
                  lessons: totalLessons,
                })}
              >
                <ChapterList chapters={chapters} />
              </Section>
            )}
          </>
        )}

        {tab === 'faq' && hasFaqTab && <FaqTab courseId={c.id} source={learningSource!} />}
        {tab === 'documents' && hasDocumentsTab && (
          <DocumentsTab courseId={c.id} source={learningSource!} />
        )}
        {tab === 'comments' && hasCommentsTab && (
          <CommentsTab courseId={c.id} source={learningSource!} />
        )}
      </div>
    </div>
  );
}

/** Backend rich-text fields (FAQ question/answer, course description) ship
 *  raw CKEditor HTML — `<img>`/`<br>`/`<p>` tags, numeric entities like
 *  `&#43;` — so they need real HTML parsing, not literal text rendering.
 *  Mirrors the course-detail page's own `RichHtml` helper. */
function RichHtml({ value }: { value?: string }) {
  if (!value) return null;
  return <>{parse(value)}</>;
}

function TabStatus({ text }: { text: string }) {
  return (
    <div
      style={{
        fontSize: 'var(--font-size-xs, 12px)',
        color: 'var(--color-text-tertiary, #6a6f73)',
        padding: '8px 0',
      }}
    >
      {text}
    </div>
  );
}

/** Wrapping row of avatar+name chips — trainers/coordinators. */
function PeopleList({ people }: { people: Array<{ id?: string; name: string; avatar?: string }> }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {people
        .filter((p) => p.name)
        .map((p, i) => (
          <div key={p.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {p.avatar ? (
              <img
                src={p.avatar}
                alt={p.name}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--radius-full, 50%)',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--radius-full, 50%)',
                  background: 'var(--color-neutral-800, #262a38)',
                  color: 'var(--color-text-on-brand, #fff)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-xs, 11px)',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {p.name.trim().charAt(0).toUpperCase()}
              </div>
            )}
            <span
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 600,
                color: 'var(--color-text-primary, #1c1d1f)',
              }}
            >
              {p.name}
            </span>
          </div>
        ))}
    </div>
  );
}

function InfoBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 'var(--font-size-xs, 11px)',
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 'var(--radius-full, 999px)',
        background: 'var(--color-surface-muted, rgba(0,0,0,0.05))',
        color: 'var(--color-text-secondary, #4a4a4a)',
        border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.1))',
      }}
    >
      {icon}
      {label}
    </span>
  );
}

function FaqTab({
  courseId,
  source,
}: {
  courseId: string;
  source: NonNullable<ReturnType<typeof useLearningSource>>;
}) {
  const { t } = useTranslation('common');
  const state = useAsyncResource(
    (signal) => source.fetchCourseFaqs!(courseId, signal),
    [courseId, source],
  );
  if (state.kind === 'loading') return <TabStatus text={t('label.loading', 'Loading…')} />;
  if (state.kind !== 'loaded' || state.data.length === 0) {
    return <TabStatus text={t('title.recordNotFound', { defaultValue: 'No records found.' })} />;
  }
  return (
    <div>
      {state.data.map((faq, i) => (
        <div key={faq.id ?? i} style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: 'var(--color-text-primary, #1c1d1f)',
              marginBottom: 4,
            }}
          >
            <RichHtml value={faq.question} />
          </div>
          <div
            style={{
              fontSize: '11.5px',
              lineHeight: 1.5,
              color: 'var(--color-text-secondary, #4a4a4a)',
            }}
          >
            <RichHtml value={faq.answer} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({
  courseId,
  source,
}: {
  courseId: string;
  source: NonNullable<ReturnType<typeof useLearningSource>>;
}) {
  const { t } = useTranslation('common');
  const state = useAsyncResource(
    (signal) => source.fetchCourseDocuments!(courseId, signal),
    [courseId, source],
  );
  if (state.kind === 'loading') return <TabStatus text={t('label.loading', 'Loading…')} />;
  if (state.kind !== 'loaded' || state.data.length === 0) {
    return <TabStatus text={t('title.recordNotFound', { defaultValue: 'No records found.' })} />;
  }
  return (
    <ul style={listStyle}>
      {state.data.map((doc, i) => (
        <li key={doc.id ?? i} style={{ marginBottom: 8 }}>
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '11.5px',
              fontWeight: 600,
              color: 'var(--color-brand-500, #3b5bdb)',
              textDecoration: 'none',
            }}
          >
            <DocumentIcon />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {doc.name}
            </span>
            {typeof doc.fileSize === 'number' && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs, 11px)',
                  fontWeight: 400,
                  color: 'var(--color-text-tertiary, #6a6f73)',
                  flexShrink: 0,
                }}
              >
                {formatFileSize(doc.fileSize)}
              </span>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}

function CommentsTab({
  courseId,
  source,
}: {
  courseId: string;
  source: NonNullable<ReturnType<typeof useLearningSource>>;
}) {
  const { t } = useTranslation('common');
  const state = useAsyncResource(
    (signal) => source.fetchCourseComments!(courseId, signal),
    [courseId, source],
  );
  if (state.kind === 'loading') return <TabStatus text={t('label.loading', 'Loading…')} />;
  if (state.kind !== 'loaded' || state.data.length === 0) {
    return <TabStatus text={t('title.recordNotFound', { defaultValue: 'No records found.' })} />;
  }
  return (
    <div>
      {state.data.map((cm, i) => (
        <div key={cm.id ?? i} style={{ marginBottom: 14, display: 'flex', gap: 8 }}>
          {cm.authorAvatar ? (
            <img
              src={cm.authorAvatar}
              alt={cm.author}
              style={{
                width: 24,
                height: 24,
                borderRadius: 'var(--radius-full, 50%)',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              aria-hidden
              style={{
                width: 24,
                height: 24,
                borderRadius: 'var(--radius-full, 50%)',
                background: 'var(--color-neutral-800, #262a38)',
                color: 'var(--color-text-on-brand, #fff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {(cm.author || '?').trim().charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  fontSize: '11.5px',
                  fontWeight: 700,
                  color: 'var(--color-text-primary, #1c1d1f)',
                }}
              >
                {cm.author}
              </span>
              {typeof cm.rating === 'number' && cm.rating > 0 && (
                <Stars value={cm.rating} size={10} />
              )}
            </div>
            <p
              style={{
                margin: 0,
                marginTop: 2,
                fontSize: '11.5px',
                lineHeight: 1.5,
                color: 'var(--color-text-secondary, #4a4a4a)',
              }}
            >
              {cm.text}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function ChapterList({ chapters }: { chapters: NonNullable<CourseCardProps['chapters']> }) {
  const [openIdx, setOpenIdx] = useState<number>(0);
  return (
    <div
      style={{
        border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.1))',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      {chapters.map((ch, i) => {
        const isOpen = i === openIdx;
        const lessonCount = ch.lessons?.length ?? 0;
        return (
          <div
            key={ch.id ?? i}
            style={{
              borderTop:
                i === 0 ? 'none' : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenIdx(isOpen ? -1 : i);
              }}
              style={{
                width: '100%',
                background: isOpen ? 'var(--color-surface-muted, rgba(0,0,0,0.03))' : 'transparent',
                color: 'var(--color-text-primary, #1c1d1f)',
                border: 'none',
                padding: 'var(--space-2, 8px) var(--space-2-5, 10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
              }}
            >
              <span
                style={{
                  fontSize: '11.5px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <ChevronIcon up={isOpen} small />
                {i + 1}. {ch.title}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: 'var(--color-text-tertiary, #6a6f73)',
                  flexShrink: 0,
                }}
              >
                {lessonCount}/{lessonCount}
              </span>
            </button>
            {isOpen && ch.lessons && ch.lessons.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  padding: 'var(--space-1, 4px) 0 var(--space-2, 8px) var(--space-7, 28px)',
                  listStyle: 'none',
                }}
              >
                {ch.lessons.map((l, li) => (
                  <li
                    key={l.id ?? li}
                    style={{
                      padding: 'var(--space-1, 4px) var(--space-2-5, 10px) var(--space-1, 4px) 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: '11px',
                      color: 'var(--color-text-secondary, #4a4a4a)',
                    }}
                  >
                    <PlayIconSmall />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i + 1}.{li + 1} {l.title}
                    </span>
                    {l.duration && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs, 12px)',
                          color: 'var(--color-text-tertiary, #6a6f73)',
                          fontVariantNumeric: 'tabular-nums',
                          flexShrink: 0,
                        }}
                      >
                        {l.duration}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h4
        style={{
          margin: 0,
          marginBottom: 6,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: 'var(--font-tracking-wider, 0.04em)',
          textTransform: 'uppercase',
          color: 'var(--color-text-primary, #1c1d1f)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        {title}
        {subtitle && (
          <span
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 500,
              color: 'var(--color-text-tertiary, #6a6f73)',
              textTransform: 'none',
              letterSpacing: 'var(--font-tracking-normal, normal)',
            }}
          >
            {subtitle}
          </span>
        )}
      </h4>
      {children}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

const listItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  fontSize: '11.5px',
  lineHeight: 1.45,
  color: 'var(--color-text-secondary, #4a4a4a)',
  marginBottom: 4,
};

function ActionButton({
  active,
  children,
  ...rest
}: {
  /** Visual "pressed" state — for toggles like watch-later/favorite. */
  active?: boolean;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { onClick: outerOnClick, ...restNoClick } = rest;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        outerOnClick?.(e);
      }}
      style={{
        width: 28,
        height: 28,
        borderRadius: 'var(--radius-full, 50%)',
        border: active ? 'none' : '1px solid var(--color-border-subtle, rgba(0,0,0,0.15))',
        background: active ? 'var(--color-brand-500, #e50914)' : 'transparent',
        color: active ? 'var(--color-text-on-brand, #fff)' : 'var(--color-text-primary, #1c1d1f)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: rest.disabled ? 'default' : 'pointer',
        opacity: rest.disabled ? 0.5 : 1,
        padding: 0,
        flexShrink: 0,
        transition: 'background 150ms ease, opacity 150ms ease',
      }}
      {...restNoClick}
    >
      {children}
    </button>
  );
}

function ProgressLine({ progress, totalHours }: { progress: number; totalHours?: number }) {
  const totalMin = totalHours ? Math.round(totalHours * 60) : null;
  const playedMin = totalMin ? Math.round(totalMin * (progress / 100)) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 3,
          background: 'rgba(0,0,0,0.12)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${clamp(progress, 0, 100)}%`,
            height: '100%',
            background: 'var(--color-brand-500, #e50914)',
          }}
        />
      </div>
      {playedMin !== null && totalMin !== null && (
        <span
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-tertiary, #6a6f73)',
            flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {playedMin} / {totalMin} min
        </span>
      )}
    </div>
  );
}

function PlayIconSmall() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ flexShrink: 0, opacity: 0.7 }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M10 8l6 4-6 4z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ChevronIcon({ up = false, small = false }: { up?: boolean; small?: boolean }) {
  const size = small ? 10 : 13;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: 'transform 200ms ease',
        transform: up ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-feedback-success, #46d369)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, marginTop: 4 }}
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function DotIcon() {
  return (
    <span
      style={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: 'var(--color-text-tertiary, #6a6f73)',
        flexShrink: 0,
        marginTop: 7,
      }}
    />
  );
}

function MuteIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M11 5L6 9H2v6h4l5 4zM23 9l-6 6M17 9l6 6" />
      </svg>
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5L6 9H2v6h4l5 4zM15.5 12a4.5 4.5 0 0 0-2.5-4M19 12a8 8 0 0 0-4-7" />
    </svg>
  );
}
